import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'

import { customFieldRepository } from '@webapp/core/data-access/repositories/CustomFieldRepository/CustomFieldRepository'
import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { ContentTypeAppLabels } from '@webapp/core/entities/internal/enums/ContentTypeAppLabelEnum'
import { ContentTypeModels } from '@webapp/core/entities/internal/enums/ContentTypeModelEnum'
import { customFieldQueryConfig } from '@webapp/core/query-configs/customField'
import { makeSelectContentTypesByModel } from '@webapp/core/stores/global'
import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import { poPdfLabelEditorQueryConfig } from '@webapp/modules/purchasing/query-configs/poPdfLabelEditor'

interface RenamePOCustomFieldMutationVariables {
  field: CustomField
  name: string
}

export const useMutateRenamePOCustomField = () => {
  const queryClient = useQueryClient()

  // Store-Sourced Redux read (not a query) — PO content-type id; the
  // CustomField entity does not carry content_type, so it is re-read here
  const poContentType = useSelector(
    makeSelectContentTypesByModel(
      ContentTypeAppLabels.PROCURIFY,
      ContentTypeModels.PO
    )
  )

  const mutation = useMutation<
    void,
    RepoError,
    RenamePOCustomFieldMutationVariables
  >({
    // PUT the full accepted payload: `name` changed, the rest carried over
    mutationFn: ({ field, name }) =>
      customFieldRepository.updateCustomField(field.id, {
        name,
        field_type: field.type,
        content_type: poContentType?.id,
        is_required: field.required,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customFieldQueryConfig.allPurchaseOrder().queryKey,
      })
      queryClient.invalidateQueries({
        queryKey: poPdfLabelEditorQueryConfig.editorFields().queryKey,
      })
    },
  })

  return {
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
  }
}
